import { Experience } from 'lib/hooks/useResumeDataQuery';

/**
 * Sort experiences in reverse chronological order:
 * 1. Present (null end_date) always first
 * 2. Then by newer end_date
 * 3. Then by newer start_date
 * 4. Stable if both dates equal (keeps user order)
 */
export function sortExperiences(items: Experience[]): Experience[] {
  const toTime = (d: string | null) => (d ? new Date(d).getTime() : null);

  return (
    items
      .map((exp, idx) => ({ ...exp, _i: idx })) // keep original index
      .sort((a, b) => {
        // 1) Handle Present vs past vs no end_date
        const aEnd = toTime(a.end_date);
        const bEnd = toTime(b.end_date);
        const aIsPresent = a.end_date === null;
        const bIsPresent = b.end_date === null;

        if (aIsPresent && !bIsPresent) return -1;
        if (!aIsPresent && bIsPresent) return 1;

        // 2) Both have past end_dates → newer first
        if (aEnd !== null && bEnd !== null && aEnd !== bEnd) {
          return bEnd - aEnd;
        }

        // 3) Fallback to start_date (newer first)
        const aStart = toTime(a.start_date) ?? -Infinity;
        const bStart = toTime(b.start_date) ?? -Infinity;
        if (aStart !== bStart) return bStart - aStart;

        // 4) Stable: preserve original order
        return a._i - b._i;
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ _i, ...exp }) => exp)
  );
}
